// Exception governance (APP-004 R3 P3, UI-P5).
//
// Types are transcribed from the frozen Policy AI contract
// (v0.3.0-governance-compliance). Approval routes, segregation of duties, cycle
// arithmetic, escalation and revocation are all backend rules: this module
// carries them across the wire and nothing more. In particular the browser
// never decides whether an actor may approve or accept risk — it asks and
// shows what the backend answers.
//
// R3 governance is additive. An exception that never touches these routes keeps
// its R2 shape (see PolicyAiClient), and `governance.legacyCompensatingControls`
// preserves the old free-text field verbatim rather than rewriting it into
// structured controls.
import { GovernanceHttp } from "./governanceHttp";
import type { GovernanceClientOptions } from "./governanceHttp";

export type ExceptionRouteMode = "sequential" | "parallel";
export type ExceptionRiskLevel = "low" | "medium" | "high" | "critical";
export type ExceptionVoteDecision = "approved" | "rejected" | "changes_requested";
export type ExceptionTargetType = "policy" | "policy_version_section" | "requirement";
export type ControlEffectiveness = "unassessed" | "effective" | "partially_effective" | "ineffective";
export type RiskAcceptanceState = "none" | "active" | "expired" | "revoked";

export interface ExceptionRouteStep {
  position: number;
  approverId: string;
}

export interface ExceptionRoute {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  mode: ExceptionRouteMode;
  steps: ExceptionRouteStep[];
  /** How many approvals a parallel route needs. Sequential routes need all steps. */
  requiredApprovals: number;
  requiresRiskAcceptance: boolean;
  isDefault: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExceptionTarget {
  id: string;
  organizationId: string;
  exceptionId: string;
  targetType: ExceptionTargetType;
  targetId: string;
  sectionKey: string | null;
  label: string;
  createdBy: string;
  createdAt: string;
}

export interface CompensatingControl {
  id: string;
  organizationId: string;
  exceptionId: string;
  title: string;
  description: string;
  ownerId: string;
  effectiveness: ControlEffectiveness;
  assessedBy: string | null;
  assessedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Assessments are only ever added: a later one never edits an earlier one. */
export interface ControlAssessment {
  id: string;
  organizationId: string;
  controlId: string;
  actorId: string;
  effectiveness: ControlEffectiveness;
  rationale: string;
  evidenceItemIds: string[];
  assessedAt: string;
}

export interface ControlEvidenceLink {
  evidenceItemId: string;
  linkRole: string;
  linkedAt: string;
}

export interface RiskAcceptance {
  id: string;
  organizationId: string;
  exceptionId: string;
  /** Acceptance belongs to one governance cycle; a renewal starts a new one. */
  governanceCycle: number;
  acceptedBy: string;
  residualRisk: ExceptionRiskLevel;
  rationale: string;
  conditions: string;
  acceptedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revocationReason: string;
}

export interface ExceptionRouteApproval {
  id: string;
  organizationId: string;
  exceptionId: string;
  routeId: string | null;
  governanceCycle: number;
  stepPosition: number;
  approverId: string;
  decision: ExceptionVoteDecision;
  rationale: string;
  decidedAt: string;
}

export interface ExceptionGovernance {
  exceptionId: string;
  routeId: string | null;
  route: ExceptionRoute | null;
  riskLevel: ExceptionRiskLevel | null;
  reviewAt: string | null;
  governanceCycle: number;
  escalatedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revocationReason: string;
  targets: ExceptionTarget[];
  controls: CompensatingControl[];
  /** The R2 free-text compensating-control field, preserved verbatim. */
  legacyCompensatingControls: string;
  riskAcceptance: RiskAcceptance | null;
  routeApprovals: ExceptionRouteApproval[];
  /** Who the backend is still waiting on for the current cycle. */
  pendingApprovers: string[];
  approvalsSatisfied: boolean;
}

export interface EscalationRunResult {
  overdue: number;
  highRisk: number;
  reviewDue: number;
  riskAcceptanceExpiring: number;
}

export interface ExceptionDashboardRow {
  exceptionId: string;
  title: string;
  status: string;
  riskLevel: ExceptionRiskLevel | null;
  approvedEnd: string | null;
  reviewAt: string | null;
  pendingApprovals: number;
  controlCount: number;
  ineffectiveControls: number;
  riskAcceptanceState: RiskAcceptanceState;
}

export interface ExceptionDashboard {
  generatedAt: string;
  totals: {
    open: number;
    approved: number;
    expired: number;
    closed: number;
    highRisk: number;
    overdue: number;
    awaitingRiskAcceptance: number;
  };
  rows: ExceptionDashboardRow[];
}

export interface CreateExceptionRouteInput {
  name: string;
  description?: string;
  mode?: ExceptionRouteMode;
  steps?: { approverId: string }[];
  requiredApprovals?: number | null;
  requiresRiskAcceptance?: boolean;
  isDefault?: boolean;
}

export interface AssignExceptionGovernanceInput {
  routeId?: string | null;
  riskLevel?: ExceptionRiskLevel | null;
  reviewAt?: string | null;
}

export interface AddExceptionTargetInput {
  targetType: ExceptionTargetType;
  targetId: string;
  sectionKey?: string | null;
}

export interface AddCompensatingControlInput {
  title: string;
  description?: string;
  ownerId: string;
}

export interface AssessControlInput {
  effectiveness: ControlEffectiveness;
  rationale?: string;
  evidenceItemIds?: string[];
}

export interface AcceptExceptionRiskInput {
  residualRisk: ExceptionRiskLevel;
  rationale: string;
  conditions?: string;
  expiresAt?: string | null;
}

export interface ExceptionVoteInput {
  decision: ExceptionVoteDecision;
  rationale?: string;
  approvedStart?: string | null;
  approvedEnd?: string | null;
  conditions?: string;
}

export interface GovernedRenewalInput {
  requestedStart: string;
  requestedEnd: string;
  reviewAt?: string | null;
}

export const CONTROL_EFFECTIVENESS_VALUES: ControlEffectiveness[] = [
  "unassessed",
  "effective",
  "partially_effective",
  "ineffective",
];

export const EXCEPTION_RISK_LEVELS: ExceptionRiskLevel[] = ["low", "medium", "high", "critical"];

/** True while the acceptance still covers the exception: not revoked, not expired. */
export function riskAcceptanceIsLive(acceptance: RiskAcceptance | null, today: string): boolean {
  if (!acceptance) return false;
  if (acceptance.revokedAt) return false;
  return acceptance.expiresAt === null || acceptance.expiresAt >= today;
}

export class ExceptionGovernanceClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions | GovernanceHttp) {
    this.http = opts instanceof GovernanceHttp ? opts : new GovernanceHttp(opts);
  }

  // ----- approval routes ----------------------------------------------------
  routes(orgId: string) {
    return this.http.get<{ routes: ExceptionRoute[] }>(orgId, "exception-routes");
  }
  createRoute(orgId: string, input: CreateExceptionRouteInput) {
    return this.http.post<{ route: ExceptionRoute }>(orgId, "exception-routes", input);
  }
  setDefaultRoute(orgId: string, routeId: string) {
    return this.http.post<{ route: ExceptionRoute }>(orgId, `exception-routes/${routeId}/default`);
  }

  // ----- governance on an exception ----------------------------------------
  /** Empty-but-present for a pre-R3 exception; never a 404 on the governance shape. */
  governance(orgId: string, exceptionId: string) {
    return this.http.get<{ governance: ExceptionGovernance }>(
      orgId,
      `policy-exceptions/${exceptionId}/governance`,
    );
  }
  assignGovernance(orgId: string, exceptionId: string, input: AssignExceptionGovernanceInput) {
    return this.http.post<{ governance: ExceptionGovernance }>(
      orgId,
      `policy-exceptions/${exceptionId}/governance`,
      input,
    );
  }

  // ----- targets ------------------------------------------------------------
  targets(orgId: string, exceptionId: string) {
    return this.http.get<{ targets: ExceptionTarget[] }>(
      orgId,
      `policy-exceptions/${exceptionId}/targets`,
    );
  }
  addTarget(orgId: string, exceptionId: string, input: AddExceptionTargetInput) {
    return this.http.post<{ target: ExceptionTarget }>(
      orgId,
      `policy-exceptions/${exceptionId}/targets`,
      input,
    );
  }
  removeTarget(orgId: string, exceptionId: string, targetId: string) {
    return this.http.del<null>(orgId, `policy-exceptions/${exceptionId}/targets/${targetId}`);
  }

  // ----- compensating controls ---------------------------------------------
  controls(orgId: string, exceptionId: string) {
    return this.http.get<{ controls: CompensatingControl[] }>(
      orgId,
      `policy-exceptions/${exceptionId}/compensating-controls`,
    );
  }
  addControl(orgId: string, exceptionId: string, input: AddCompensatingControlInput) {
    return this.http.post<{ control: CompensatingControl }>(
      orgId,
      `policy-exceptions/${exceptionId}/compensating-controls`,
      input,
    );
  }
  assessments(orgId: string, controlId: string) {
    return this.http.get<{ assessments: ControlAssessment[] }>(
      orgId,
      `compensating-controls/${controlId}/assessments`,
    );
  }
  /**
   * Refused with 403 when the backend finds a segregation-of-duties conflict —
   * the owner of a control cannot assess it while the assessment can still move
   * the approval decision. That rule is not mirrored here.
   */
  assessControl(orgId: string, controlId: string, input: AssessControlInput) {
    return this.http.post<{ control: CompensatingControl; assessment: ControlAssessment }>(
      orgId,
      `compensating-controls/${controlId}/assessments`,
      input,
    );
  }
  controlEvidence(orgId: string, controlId: string) {
    return this.http.get<{ evidence: ControlEvidenceLink[] }>(
      orgId,
      `compensating-controls/${controlId}/evidence`,
    );
  }

  // ----- risk acceptance ----------------------------------------------------
  riskAcceptances(orgId: string, exceptionId: string) {
    return this.http.get<{ riskAcceptances: RiskAcceptance[] }>(
      orgId,
      `policy-exceptions/${exceptionId}/risk-acceptances`,
    );
  }
  /** Needs `policy_ai.risk.accept`, which is not implied by approval rights. */
  acceptRisk(orgId: string, exceptionId: string, input: AcceptExceptionRiskInput) {
    return this.http.post<{ riskAcceptance: RiskAcceptance }>(
      orgId,
      `policy-exceptions/${exceptionId}/risk-acceptances`,
      input,
    );
  }
  revokeAcceptance(orgId: string, acceptanceId: string, reason: string) {
    return this.http.post<{ riskAcceptance: RiskAcceptance }>(
      orgId,
      `risk-acceptances/${acceptanceId}/revoke`,
      { reason },
    );
  }

  // ----- routed decisions ---------------------------------------------------
  /**
   * One approver's vote against the configured route. The R2 exception decision
   * is applied only once the route is satisfied, and a stale view is answered
   * with 409 CONCURRENT_STATE_CHANGE rather than a silent overwrite.
   */
  vote(orgId: string, exceptionId: string, input: ExceptionVoteInput) {
    return this.http.post<{ governance: ExceptionGovernance }>(
      orgId,
      `policy-exceptions/${exceptionId}/route-approvals`,
      input,
    );
  }

  // ----- renewal and revocation --------------------------------------------
  /** Creates a successor exception and opens a new governance cycle. */
  renew(orgId: string, exceptionId: string, input: GovernedRenewalInput) {
    return this.http.post<{ exception: { id: string }; governance: ExceptionGovernance }>(
      orgId,
      `policy-exceptions/${exceptionId}/governed-renewals`,
      input,
    );
  }
  revoke(orgId: string, exceptionId: string, reason: string) {
    return this.http.post<{ governance: ExceptionGovernance }>(
      orgId,
      `policy-exceptions/${exceptionId}/revocation`,
      { reason },
    );
  }

  // ----- escalation and dashboard ------------------------------------------
  /** Deterministic and idempotent: a repeated run raises nothing further. */
  runEscalations(orgId: string) {
    return this.http.post<{ escalations: EscalationRunResult }>(
      orgId,
      "exception-governance/escalations/run",
      {},
    );
  }
  dashboard(orgId: string) {
    return this.http.get<{ dashboard: ExceptionDashboard }>(
      orgId,
      "exception-governance/dashboard",
    );
  }
}
